/** Einfacher, unveränderlicher 3D-Vektor (Z = oben, wie SketchUp). */
export interface Vec3 {
  readonly x: number;
  readonly y: number;
  readonly z: number;
}

export const EPS = 1e-6;

export function vec(x = 0, y = 0, z = 0): Vec3 {
  return { x, y, z };
}

export function add(a: Vec3, b: Vec3): Vec3 {
  return { x: a.x + b.x, y: a.y + b.y, z: a.z + b.z };
}

export function sub(a: Vec3, b: Vec3): Vec3 {
  return { x: a.x - b.x, y: a.y - b.y, z: a.z - b.z };
}

export function mul(a: Vec3, s: number): Vec3 {
  return { x: a.x * s, y: a.y * s, z: a.z * s };
}

export function dot(a: Vec3, b: Vec3): number {
  return a.x * b.x + a.y * b.y + a.z * b.z;
}

export function cross(a: Vec3, b: Vec3): Vec3 {
  return {
    x: a.y * b.z - a.z * b.y,
    y: a.z * b.x - a.x * b.z,
    z: a.x * b.y - a.y * b.x
  };
}

export function len(a: Vec3): number {
  return Math.hypot(a.x, a.y, a.z);
}

export function dist(a: Vec3, b: Vec3): number {
  return len(sub(a, b));
}

export function distSq(a: Vec3, b: Vec3): number {
  const d = sub(a, b);
  return d.x * d.x + d.y * d.y + d.z * d.z;
}

export function normalize(a: Vec3): Vec3 {
  const l = len(a);
  return l < EPS ? vec(0, 0, 0) : { x: a.x / l, y: a.y / l, z: a.z / l };
}

export function lerp(a: Vec3, b: Vec3, t: number): Vec3 {
  return { x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t, z: a.z + (b.z - a.z) * t };
}

export function midpoint(a: Vec3, b: Vec3): Vec3 {
  return lerp(a, b, 0.5);
}

export function eq(a: Vec3, b: Vec3, eps = EPS): boolean {
  return Math.abs(a.x - b.x) < eps && Math.abs(a.y - b.y) < eps && Math.abs(a.z - b.z) < eps;
}

/** Mittelwert einer Punktmenge (Schwerpunkt). */
export function centroid(points: Vec3[]): Vec3 {
  if (points.length === 0) return vec();
  let x = 0, y = 0, z = 0;
  for (const p of points) { x += p.x; y += p.y; z += p.z; }
  return { x: x / points.length, y: y / points.length, z: z / points.length };
}

export const AXIS_X: Vec3 = vec(1, 0, 0);
export const AXIS_Y: Vec3 = vec(0, 1, 0);
export const AXIS_Z: Vec3 = vec(0, 0, 1);
export const AXES = [AXIS_X, AXIS_Y, AXIS_Z] as const;

/** Nächste Weltachse zur Richtung (0=X rot, 1=Y grün, 2=Z blau) plus Winkelabweichung in Radiant. */
export function nearestAxis(dir: Vec3): { axis: 0 | 1 | 2; angle: number } {
  const d = normalize(dir);
  let best: { axis: 0 | 1 | 2; angle: number } = { axis: 0, angle: Math.PI };
  for (let i = 0; i < 3; i++) {
    for (const sign of [1, -1]) {
      const a = Math.acos(clamp(dot(d, mul(AXES[i]!, sign)), -1, 1));
      if (a < best.angle) best = { axis: i as 0 | 1 | 2, angle: a };
    }
  }
  return best;
}

export function clamp(v: number, min: number, max: number): number {
  return v < min ? min : v > max ? max : v;
}

/** Projiziert einen Punkt auf eine durch Punkt+Normale definierte Ebene entlang der Kamerablickrichtung. */
export function projectPointOnPlane(point: Vec3, dir: Vec3, planePoint: Vec3, planeNormal: Vec3): Vec3 | null {
  const denom = dot(dir, planeNormal);
  if (Math.abs(denom) < EPS) return null;
  const t = dot(sub(planePoint, point), planeNormal) / denom;
  if (!Number.isFinite(t)) return null;
  return add(point, mul(dir, t));
}

/** Kürzester Abstand Punkt → Strecke sowie der Fußpunkt auf der Strecke. */
export function closestPointOnSegment(p: Vec3, a: Vec3, b: Vec3): { point: Vec3; t: number; distance: number } {
  const ab = sub(b, a);
  const abLenSq = dot(ab, ab);
  if (abLenSq < EPS) {
    return { point: a, t: 0, distance: dist(p, a) };
  }
  const t = clamp(dot(sub(p, a), ab) / abLenSq, 0, 1);
  const point = add(a, mul(ab, t));
  return { point, t, distance: dist(p, point) };
}

/** Achse, entlang der ein Delta überwiegend verläuft (null bei keiner Bewegung). */
export function dominantAxis(d: Vec3): 0 | 1 | 2 | null {
  const ax = Math.abs(d.x), ay = Math.abs(d.y), az = Math.abs(d.z);
  if (Math.max(ax, ay, az) < 1e-9) return null;
  return ax >= ay && ax >= az ? 0 : ay >= az ? 1 : 2;
}
